import { useState, useCallback } from 'react'
import req from '@sportninja/common/api/request'

export interface GameEditData {
  home_team_id?: number
  visiting_team_id?: number
  venue_id?: number
  facility_id?: number
  starts_at?: string
  ends_at?: string
  target_date?: string
}

export interface EvaluationResult {
  satisfaction_score: number
  violations: Array<{
    constraint_type: string
    constraint_name: string | null
    severity: 'error' | 'warning' | 'info'
    message: string
    affected_game_numbers: number[]
    affected_team_uids: string[]
  }>
  hard_violations: number
  soft_violations: number
  games_evaluated: number
}

export interface UseGameEditResult {
  editingGameId: string | null
  saving: boolean
  approving: boolean
  error: string | null
  lastEvaluation: EvaluationResult | null
  startEditing: (gameId: string) => void
  cancelEditing: () => void
  saveGame: (
    requestId: string,
    gameId: string,
    data: GameEditData
  ) => Promise<boolean>
  approveGame: (requestId: string, gameId: string) => Promise<boolean>
  unapproveGame: (requestId: string, gameId: string) => Promise<boolean>
  rejectGame: (
    requestId: string,
    gameId: string,
    reason: string
  ) => Promise<boolean>
  approveAllGames: (requestId: string) => Promise<boolean>
  approveByDivisions: (
    requestId: string,
    divisionUids: string[]
  ) => Promise<boolean>
  bulkApproveGames: (requestId: string, gameUids: string[]) => Promise<boolean>
  bulkRevertGames: (requestId: string, gameUids: string[]) => Promise<boolean>
  revertByDivisions: (
    requestId: string,
    divisionUids: string[]
  ) => Promise<boolean>
  clearError: () => void
}

export const useGameEdit = (): UseGameEditResult => {
  const [editingGameId, setEditingGameId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [approving, setApproving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastEvaluation, setLastEvaluation] = useState<EvaluationResult | null>(
    null
  )

  const startEditing = useCallback((gameId: string) => {
    setEditingGameId(gameId)
    setError(null)
  }, [])

  const cancelEditing = useCallback(() => {
    setEditingGameId(null)
    setError(null)
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const saveGame = useCallback(
    async (
      requestId: string,
      gameId: string,
      data: GameEditData
    ): Promise<boolean> => {
      setSaving(true)
      setError(null)

      try {
        const responseBody = await req(
          `/scheduler/requests/${requestId}/games/${gameId}`,
          {
            method: 'PUT',
            body: JSON.stringify(data),
          }
        )

        const evaluation = responseBody?.evaluation
        if (evaluation) {
          setLastEvaluation(evaluation)
        }

        setEditingGameId(null)
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : (err as { message?: string })?.message ||
              'Failed to save game changes'
        setError(message)
        return false
      } finally {
        setSaving(false)
      }
    },
    []
  )

  const approveGame = useCallback(
    async (requestId: string, gameId: string): Promise<boolean> => {
      setApproving(true)
      setError(null)

      try {
        await req(`/scheduler/requests/${requestId}/games/${gameId}/approve`, {
          method: 'POST',
        })
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to approve game'
        setError(message)
        return false
      } finally {
        setApproving(false)
      }
    },
    []
  )

  const unapproveGame = useCallback(
    async (requestId: string, gameId: string): Promise<boolean> => {
      setApproving(true)
      setError(null)

      try {
        await req(
          `/scheduler/requests/${requestId}/games/${gameId}/unapprove`,
          { method: 'POST' }
        )
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to unapprove game'
        setError(message)
        return false
      } finally {
        setApproving(false)
      }
    },
    []
  )

  const rejectGame = useCallback(
    async (
      requestId: string,
      gameId: string,
      reason: string
    ): Promise<boolean> => {
      setApproving(true)
      setError(null)

      try {
        await req(`/scheduler/requests/${requestId}/games/${gameId}/reject`, {
          method: 'POST',
          body: JSON.stringify({ reason }),
        })
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to reject game'
        setError(message)
        return false
      } finally {
        setApproving(false)
      }
    },
    []
  )

  const approveAllGames = useCallback(
    async (requestId: string): Promise<boolean> => {
      setApproving(true)
      setError(null)

      try {
        await req(`/scheduler/requests/${requestId}/games/approve-all`, {
          method: 'POST',
        })
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to approve all games'
        setError(message)
        return false
      } finally {
        setApproving(false)
      }
    },
    []
  )

  const approveByDivisions = useCallback(
    async (requestId: string, divisionUids: string[]): Promise<boolean> => {
      setApproving(true)
      setError(null)

      try {
        await req(
          `/scheduler/requests/${requestId}/games/approve-by-divisions`,
          {
            method: 'POST',
            body: JSON.stringify({ division_uids: divisionUids }),
          }
        )
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to approve games by divisions'
        setError(message)
        return false
      } finally {
        setApproving(false)
      }
    },
    []
  )

  const bulkApproveGames = useCallback(
    async (requestId: string, gameUids: string[]): Promise<boolean> => {
      setApproving(true)
      setError(null)

      try {
        await req(`/scheduler/requests/${requestId}/games/bulk-approve`, {
          method: 'POST',
          body: JSON.stringify({ game_uids: gameUids }),
        })
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to bulk approve games'
        setError(message)
        return false
      } finally {
        setApproving(false)
      }
    },
    []
  )

  const bulkRevertGames = useCallback(
    async (requestId: string, gameUids: string[]): Promise<boolean> => {
      setApproving(true)
      setError(null)

      try {
        await req(`/scheduler/requests/${requestId}/games/bulk-revert`, {
          method: 'POST',
          body: JSON.stringify({ game_uids: gameUids }),
        })
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to bulk revert games'
        setError(message)
        return false
      } finally {
        setApproving(false)
      }
    },
    []
  )

  const revertByDivisions = useCallback(
    async (requestId: string, divisionUids: string[]): Promise<boolean> => {
      setApproving(true)
      setError(null)

      try {
        await req(
          `/scheduler/requests/${requestId}/games/revert-by-divisions`,
          {
            method: 'POST',
            body: JSON.stringify({ division_uids: divisionUids }),
          }
        )
        return true
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to revert games by divisions'
        setError(message)
        return false
      } finally {
        setApproving(false)
      }
    },
    []
  )

  return {
    editingGameId,
    saving,
    approving,
    error,
    lastEvaluation,
    startEditing,
    cancelEditing,
    saveGame,
    approveGame,
    unapproveGame,
    rejectGame,
    approveAllGames,
    approveByDivisions,
    bulkApproveGames,
    bulkRevertGames,
    revertByDivisions,
    clearError,
  }
}

export default useGameEdit
